import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from "recharts";
import { cn } from "@/lib/utils";
import { SectionHeader } from "./SectionHeader";
import type { FactorKey } from "./types";

export interface DistributionItem {
  moduleId: string;
  key: FactorKey;
  label: string;
  value: number;
  max: number;
  color: string;
}

interface ScoringWeightsCardProps {
  distributionItems: DistributionItem[];
  distributionTotal: number;
  scrollToModule: (id: string) => void;
}

export function ScoringWeightsCard({ distributionItems, distributionTotal, scrollToModule }: ScoringWeightsCardProps) {
  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <SectionHeader title="Scoring Weights" description="Each factor contributes a percentage of the final KPS score. All weights add up to 100%." />

        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6 items-center">
          <div className="relative h-[240px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={[
                    ...distributionItems,
                    ...(distributionTotal < 100
                      ? [{ id: "__remainder", label: "Unallocated", value: 100 - distributionTotal, color: "bg-muted", isRemainder: true }]
                      : []),
                  ]}
                  dataKey="value"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={100}
                  paddingAngle={2}
                  onClick={(d: { moduleId?: string; isRemainder?: boolean }) => {
                    if (d?.moduleId && !d.isRemainder) scrollToModule(d.moduleId);
                  }}
                  className="cursor-pointer focus:outline-none"
                >
                  {distributionItems.map((item) => (
                    <Cell
                      key={item.moduleId}
                      fill={`hsl(var(--${item.color.replace("bg-", "")}))`}
                      className="hover:opacity-80 transition-opacity focus:outline-none"
                      stroke="hsl(var(--background))"
                      strokeWidth={2}
                    />
                  ))}
                  {distributionTotal < 100 && (
                    <Cell
                      key="__remainder"
                      fill="hsl(var(--muted))"
                      stroke="hsl(var(--background))"
                      strokeWidth={2}
                    />
                  )}
                </Pie>
                <RechartsTooltip
                  wrapperStyle={{ zIndex: 10 }}
                  contentStyle={{
                    background: "hsl(var(--background))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                  formatter={(value: number, _name: string, entry: { payload?: { label?: string } }) => [
                    `${value}%`,
                    entry?.payload?.label,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className={cn(
                "text-2xl font-semibold tabular-nums",
                distributionTotal === 100 ? "text-foreground" : "text-status-error-strong"
              )}>
                {distributionTotal}%
              </span>
              <span className="text-xs text-muted-foreground">Total</span>
            </div>
          </div>

          <div className="space-y-1.5">
            {distributionItems.map((item) => (
              <button
                key={item.moduleId}
                type="button"
                onClick={() => scrollToModule(item.moduleId)}
                className="w-full flex items-center gap-3 rounded-md py-1.5 px-2 -mx-2 hover:bg-muted/50 focus:outline-none focus:bg-muted/50 transition-colors text-left"
              >
                <div className={cn("h-2.5 w-2.5 rounded-full shrink-0", item.color)} />
                <span className="text-sm text-foreground flex-1">{item.label}</span>
                <span className="text-sm tabular-nums text-foreground font-medium">{item.value}%</span>
              </button>
            ))}
          </div>
        </div>

        <Alert variant="info">
          <Info className="h-4 w-4" />
          <AlertDescription>
            Adjust each weight using the <span className="font-semibold">Scoring Weight</span> field in the top right of each module below.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}
